/**
 * Feature 1 (#157) - AgreementInteractionsTab.
 *
 * Renders inside AgreementDetailPage's "Interactions" tab. Shows the list
 * of notes filed on this Agreement (visibility-scoped server-side), with
 * a [+ New interaction] button, Edit / Delete actions (author-only),
 * and Restore (admin/compliance only) on deleted rows.
 *
 * Author-only rules (2026-05-28):
 *   - Edit: only the author. UPDATE is direct (no maker-checker).
 *   - Delete: only the author. Routes through maker-checker. Maker filings
 *     stay pending; Checker filings auto-apply but still leave a CR row.
 *   - Compliance / Admin CANNOT directly delete a note authored by someone
 *     else — they review the author's deletion request from the CR queue.
 */
import {
  ActionIcon, Badge, Button, Group, Pagination, Stack, Switch, Table, Text, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { StatusExplained } from './StatusExplained';
import {
  IconArrowBackUp, IconEdit, IconEye, IconFilterOff, IconLock, IconPlus,
  IconTrash, IconUsersGroup,
} from '@tabler/icons-react';
import * as React from 'react';

import { useCurrentUser } from '../hooks/useAuth';
import {
  useAgreementInteractions, useDeleteAgreementInteraction,
  useRestoreAgreementInteraction, useStaffOptions,
} from '../hooks/useAgreementInteractions';
import { useAgreementParties } from '../hooks/useAgreementParties';
import type {
  AgreementInteraction, AgreementInteractionFilters, InteractionKind, UUID,
} from '../api/types';
import { INTERACTION_KIND_LABELS } from '../api/types';
import {
  ColumnDateRangeFilter, ColumnMultiSelectFilter, ColumnSelectFilter,
  ColumnTextFilter, FilteredColumnLabel, hasActiveFilters,
} from './ColumnFilters';
import { PendingCRBadge } from './PendingCRBadge';
import { InteractionFormModal } from './InteractionFormModal';
import { InteractionViewModal } from './InteractionViewModal';
import { useAlertModal } from './AlertModalProvider';
import { useConfirmDelete } from '../hooks/useConfirmDelete';

const KIND_FILTER_OPTIONS = (Object.keys(INTERACTION_KIND_LABELS) as InteractionKind[])
  .map((k) => ({ value: k, label: INTERACTION_KIND_LABELS[k] }));

interface Props {
  agreementId: UUID;
}

function formatDate(iso: string | null | undefined): string {
  return iso ? iso.substring(0, 10) : '—';
}
function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return iso.substring(0, 10) + ' ' + iso.substring(11, 16);
}

function AttendeesCell({ item }: { item: AgreementInteraction }) {
  const persons = item.person_ids.map((pid) => item.person_labels[pid] ?? pid.substring(0, 8));
  const users = item.user_ids.map((uid) => item.user_labels[uid] ?? uid.substring(0, 8));
  const hasAny = persons.length > 0 || users.length > 0 || item.has_other_attendees;
  if (!hasAny) {
    return <Text size="sm" c="dimmed">—</Text>;
  }
  return (
    <Group gap={6} wrap="wrap">
      {persons.map((name, i) => (
        <Badge key={`p-${i}`} variant="light" color="blue" size="sm">{name}</Badge>
      ))}
      {users.map((name, i) => (
        <Badge key={`u-${i}`} variant="light" color="grape" size="sm">{name}</Badge>
      ))}
      {item.has_other_attendees && (
        <Tooltip label="External attendees described in the body" withArrow>
          <Badge variant="outline" color="gray" size="sm"
            styles={{ root: { fontStyle: 'italic', cursor: 'help' } }}>
            + Other
          </Badge>
        </Tooltip>
      )}
    </Group>
  );
}

const INTERACTIONS_PAGE_SIZE = 50;

export function AgreementInteractionsTab({ agreementId }: Props) {
  const { user } = useCurrentUser();
  const isChecker = user?.role === 'admin' || user?.role === 'compliance';
  // Admins hold every privilege over every interaction, whoever wrote it (Pedro
  // 2026-10-10, the same rule as tasks) — mirrors `_is_admin` in the backend router.
  const isAdmin = user?.role === 'admin';

  const [showDeleted, setShowDeleted] = React.useState(false);
  const includeDeleted = isChecker && showDeleted;

  // ── #41 (2026-06-12) per-column filters ───────────────────────────────
  // All controlled here; every value traces to a query param (C-131).
  // Attendee encodes the person/user split as 'p:<id>' / 'u:<id>'.
  const [kindF, setKindF] = React.useState<string[]>([]);
  const [dateFrom, setDateFrom] = React.useState('');
  const [dateTo, setDateTo] = React.useState('');
  const [qText, setQText] = React.useState('');
  const [attendeeF, setAttendeeF] = React.useState<string | null>(null);
  const [authorF, setAuthorF] = React.useState<string | null>(null);

  const filtersActive = hasActiveFilters({
    kindF, dateFrom, dateTo, qText, attendeeF, authorF,
  });
  // Per-column active flags drive the FilteredColumnLabel highlight
  // (Pedro's polish 2026-06-12: a filtered column must be visibly marked).
  const dateActive = dateFrom !== '' || dateTo !== '';
  function clearFilters() {
    setKindF([]); setDateFrom(''); setDateTo('');
    setQText(''); setAttendeeF(null); setAuthorF(null);
  }

  // Fresh navigation = no filters (Pedro's polish 2026-06-12). React keeps
  // this component mounted when only the route param changes (agreement A
  // → agreement B), so state would otherwise leak across agreements.
  React.useEffect(() => {
    clearFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agreementId]);

  const filters: AgreementInteractionFilters = {
    kind: kindF.length > 0 ? (kindF as InteractionKind[]) : undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    q: qText || undefined,
    person_id: attendeeF?.startsWith('p:') ? attendeeF.slice(2) : undefined,
    user_id: attendeeF?.startsWith('u:') ? attendeeF.slice(2) : undefined,
    author_id: authorF ?? undefined,
  };

  // Option sources mirror InteractionFormModal: parties on this Agreement
  // (server-resolved names, #231 pattern — no per-row Person fetches) and
  // the staff list. Authors are staff by definition.
  const partiesQ = useAgreementParties({ agreement_id: agreementId, limit: 200 });
  const staffQ = useStaffOptions();
  const attendeeOptions = React.useMemo(() => {
    const rawParties = partiesQ.data?.items;
    const parties = Array.isArray(rawParties) ? rawParties : [];
    const seen = new Set<string>();
    const personItems: { value: string; label: string }[] = [];
    for (const ap of parties) {
      if (!ap.person_id || seen.has(ap.person_id)) continue;
      seen.add(ap.person_id);
      personItems.push({
        value: `p:${ap.person_id}`,
        label: ap.person_display_name ?? `(${ap.person_id.slice(0, 8)})`,
      });
    }
    const staff = Array.isArray(staffQ.data) ? staffQ.data : [];
    return [
      { group: 'Clients / parties', items: personItems },
      { group: 'Baluarte staff', items: staff.map((u) => ({ value: `u:${u.id}`, label: u.display_name })) },
    ];
  }, [partiesQ.data, staffQ.data]);
  const authorOptions = React.useMemo(() => {
    const staff = Array.isArray(staffQ.data) ? staffQ.data : [];
    return staff.map((u) => ({ value: u.id, label: u.display_name }));
  }, [staffQ.data]);

  // Paging (C-374): the tab showed only the first 50 interactions, with no way to see more.
  const [page, setPage] = React.useState(1);
  const filterKey = JSON.stringify([includeDeleted, filters]);
  React.useEffect(() => { setPage(1); }, [filterKey]);
  const q = useAgreementInteractions(agreementId, {
    include_deleted: includeDeleted, filters,
    limit: INTERACTIONS_PAGE_SIZE, offset: (page - 1) * INTERACTIONS_PAGE_SIZE,
  });
  const totalPages = q.data ? Math.max(1, Math.ceil(q.data.total / INTERACTIONS_PAGE_SIZE)) : 1;
  const del = useDeleteAgreementInteraction();
  const restore = useRestoreAgreementInteraction();
  const confirmDelete = useConfirmDelete();
  const { showAlert } = useAlertModal();

  const [modalOpen, setModalOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<AgreementInteraction | null>(null);
  // Read-only view, open to ANY user who can see the row (the full body is
  // otherwise reachable only via author-gated Edit). Edit stays author-only.
  const [viewing, setViewing] = React.useState<AgreementInteraction | null>(null);

  const items = q.data?.items ?? [];

  function openCreate() {
    setEditing(null);
    setModalOpen(true);
  }
  function openView(item: AgreementInteraction) {
    setViewing(item);
  }
  function openEdit(item: AgreementInteraction) {
    if (item.created_by !== user?.id && !isAdmin) {
      showAlert({
        color: 'orange',
        title: 'Cannot edit',
        message: 'Only the author of a note or an Admin can edit it. File a corrective note instead.',
      });
      return;
    }
    setEditing(item);
    setModalOpen(true);
  }
  function handleDelete(item: AgreementInteraction) {
    confirmDelete('interaction', () => del.mutate(item.id, {
      onSuccess: () => {
        if (!isChecker) {
          showAlert({
            color: 'orange',
            title: 'Filed for compliance approval',
            message: 'Your delete request was filed. The interaction remains visible until Compliance approves.',
          });
        }
      },
    }));
  }
  function handleRestore(item: AgreementInteraction) {
    restore.mutate(item.id, {
      onSuccess: () => {
        notifications.show({ message: 'Interaction restored', color: 'green' });
      },
      onError: (err) => {
        showAlert({
          color: 'red',
          title: 'Could not restore',
          message: err.detail ?? String(err),
        });
      },
    });
  }

  return (
    <Stack>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Text size="sm" c="dimmed">
          Meetings, calls and other interactions logged against this Agreement.
          Private notes are visible only to you, Compliance and Admin.
        </Text>
        <Group gap="md" wrap="nowrap">
          {isChecker && (
            <Tooltip label="Admin / Compliance only. Show interactions that were soft-deleted, so you can restore them." withArrow>
              <Switch size="sm" label="Show deleted"
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.currentTarget.checked)} />
            </Tooltip>
          )}
          <Button size="sm" leftSection={<IconPlus size={14} />} onClick={openCreate}>
            New interaction
          </Button>
        </Group>
      </Group>

      {q.isLoading && <Text c="dimmed" size="sm">Loading…</Text>}
      {/* #41 / C-131: the two zero-row states are distinct — "nothing
          filed yet" hides the table; "nothing MATCHES" keeps the table
          (and its filter controls) visible with an in-table hint row. */}
      {!q.isLoading && items.length === 0 && !filtersActive && (
        <Text c="dimmed" size="sm">
          No interactions logged yet. Click "New interaction" to file one.
        </Text>
      )}

      {!q.isLoading && (items.length > 0 || filtersActive) && (
        <Table withTableBorder striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={130}>
                <FilteredColumnLabel active={dateActive}>Date</FilteredColumnLabel>
              </Table.Th>
              <Table.Th w={110}>
                <FilteredColumnLabel active={kindF.length > 0}>Kind</FilteredColumnLabel>
              </Table.Th>
              <Table.Th>
                <FilteredColumnLabel active={!!qText}>Title</FilteredColumnLabel>
              </Table.Th>
              {/* #40/old-#333 (2026-06-11): explicit width so attendee
                  badges get room instead of wrapping into tall stacks
                  (body text is already lineClamp-bounded). */}
              <Table.Th w={300}>
                <FilteredColumnLabel active={!!attendeeF}>
                  <Group gap={4} wrap="nowrap"><IconUsersGroup size={14} /><span>Attendees</span></Group>
                </FilteredColumnLabel>
              </Table.Th>
              <Table.Th w={160}>
                <FilteredColumnLabel active={!!authorF}>Author · Filed</FilteredColumnLabel>
              </Table.Th>
              <Table.Th w={80}>Privacy</Table.Th>
              <Table.Th w={130}>Actions</Table.Th>
            </Table.Tr>
            {/* #41 Option B (Pedro 2026-06-12): per-column filter row.
                Controls are the reusable ColumnFilters primitives. */}
            <Table.Tr>
              <Table.Th>
                <ColumnDateRangeFilter from={dateFrom} to={dateTo}
                  onChange={(f, t) => { setDateFrom(f); setDateTo(t); }}
                  ariaLabelPrefix="Interaction date" />
              </Table.Th>
              <Table.Th>
                <ColumnMultiSelectFilter values={kindF} onChange={setKindF}
                  options={KIND_FILTER_OPTIONS} allLabel="All" ariaLabel="Filter by kind" />
              </Table.Th>
              <Table.Th>
                <ColumnTextFilter value={qText} onChange={setQText}
                  placeholder="Search title & body…" ariaLabel="Search title and body" />
              </Table.Th>
              <Table.Th>
                <ColumnSelectFilter value={attendeeF} onChange={setAttendeeF}
                  options={attendeeOptions} allLabel="Any attendee" ariaLabel="Filter by attendee" />
              </Table.Th>
              <Table.Th>
                <ColumnSelectFilter value={authorF} onChange={setAuthorF}
                  options={authorOptions} allLabel="Any author" ariaLabel="Filter by author" />
              </Table.Th>
              <Table.Th />
              <Table.Th>
                {filtersActive && (
                  <Tooltip label="Clear all filters" withArrow>
                    <ActionIcon variant="subtle" color="gray" onClick={clearFilters}
                      aria-label="Clear all filters">
                      <IconFilterOff size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.length === 0 && filtersActive && (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Group gap="xs" justify="center" py="sm">
                    <Text c="dimmed" size="sm">
                      No interactions match the current filters.
                    </Text>
                    <Button size="compact-xs" variant="subtle"
                      leftSection={<IconFilterOff size={14} />}
                      onClick={clearFilters}>
                      Clear filters
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            )}
            {items.map((item) => {
              const canManage = item.created_by === user?.id || isAdmin;   // edit / delete
              const isDeleted = !!item.deleted_at;
              return (
                <Table.Tr key={item.id}
                  style={isDeleted ? { opacity: 0.55 } : undefined}>
                  <Table.Td>
                    <Text size="sm" ff="monospace"
                      td={isDeleted ? 'line-through' : undefined}>
                      {formatDate(item.interaction_date)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {/* 0035 (#41a0): neutral badge — kind is categorical,
                        not a state, so it stays outside the §3j state
                        colour vocabulary (orange/blue/green/red/violet). */}
                    <Badge variant="default" size="sm">
                      {INTERACTION_KIND_LABELS[item.kind] ?? item.kind}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Stack gap={2}>
                      <Group gap={6}>
                        <Text size="sm" fw={500} c="blue"
                          td={isDeleted ? 'line-through' : undefined}
                          style={{ cursor: 'pointer' }}
                          onClick={() => openView(item)}>
                          {item.title}
                        </Text>
                        {isDeleted && (
                          <StatusExplained entity="interaction" status="Deleted">
                            <Badge color="red" variant="light" size="sm">Deleted</Badge>
                          </StatusExplained>
                        )}
                        {/* B1/#35 (2026-06-11, old #364): pending-CR badge
                            on lower-traffic lists too. */}
                        <PendingCRBadge crs={item.pending_change_requests} />
                      </Group>
                      {item.body && (
                        <Text size="xs" c="dimmed" lineClamp={2}>{item.body}</Text>
                      )}
                    </Stack>
                  </Table.Td>
                  <Table.Td><AttendeesCell item={item} /></Table.Td>
                  <Table.Td>
                    <Stack gap={2}>
                      <Text size="sm">{item.created_by_label ?? '—'}</Text>
                      <Text size="xs" c="dimmed" ff="monospace">
                        {formatDateTime(item.created_at)}
                      </Text>
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    {item.is_private && (
                      <Tooltip label="Private — visible only to you, Compliance, Admin">
                        <Badge color="gray" variant="light" leftSection={<IconLock size={11} />}>
                          Private
                        </Badge>
                      </Tooltip>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <Tooltip label="View full interaction" withArrow>
                        <ActionIcon variant="subtle" color="gray"
                          onClick={() => openView(item)}>
                          <IconEye size={14} />
                        </ActionIcon>
                      </Tooltip>
                      {isDeleted ? (
                        isChecker && (
                          <Tooltip label="Restore this interaction" withArrow>
                            <ActionIcon variant="subtle" color="green"
                              onClick={() => handleRestore(item)}
                              loading={restore.isPending}>
                              <IconArrowBackUp size={14} />
                            </ActionIcon>
                          </Tooltip>
                        )
                      ) : (
                        <>
                          <Tooltip label={canManage ? 'Edit' : 'Only the author or an Admin can edit'}>
                            <ActionIcon variant="subtle" color="blue"
                              disabled={!canManage}
                              onClick={() => openEdit(item)}>
                              <IconEdit size={14} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label={
                            canManage
                              ? (isChecker
                                  ? 'Delete (files change request, auto-approved)'
                                  : 'Delete (files change request)')
                              : "Only the author or an Admin can delete a note. An author's request is approved by Compliance / Admin."
                          }>
                            <ActionIcon variant="subtle" color="red"
                              disabled={!canManage}
                              onClick={() => handleDelete(item)}
                              loading={del.isPending}>
                              <IconTrash size={14} />
                            </ActionIcon>
                          </Tooltip>
                        </>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      )}

      {q.data && q.data.total > INTERACTIONS_PAGE_SIZE && (
        <Group justify="space-between">
          <Text size="sm" c="dimmed">{q.data.total} total</Text>
          <Pagination total={totalPages} value={page} onChange={setPage} />
        </Group>
      )}

      <InteractionFormModal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        agreementId={agreementId}
        editing={editing}
      />
      <InteractionViewModal
        opened={!!viewing}
        onClose={() => setViewing(null)}
        interaction={viewing}
      />
    </Stack>
  );
}
